import { useState } from 'react';
import { Pill } from '@/components/Pill';
import { compColorClass } from '@/lib/compColor';
import { formatComp, formatDollars } from '@/lib/useRetailStores';
import {
  deriveLeaderboard,
  sortAllStores,
  useHomeLeaderboard,
  type HomeFilters,
  type LeaderRow,
} from '@/lib/useHomeDashboard';
import { TileMessage, TileShell, TileSkeleton } from './TileShell';
import { MUTED_TEXT, NOT_AVAILABLE } from './tileText';

function BarRow({ row, scale }: { row: LeaderRow; scale: number }) {
  const comp = row.comp;
  const widthPct = comp != null && scale > 0 ? Math.min(100, (Math.abs(comp) / scale) * 100) : 0;
  const positive = comp != null && comp >= 0;
  const tip = `${row.store_name}: ${formatDollars(row.ty)}, ${formatComp(comp)} vs last year`;
  return (
    <li className="flex items-center gap-2" title={tip} aria-label={tip}>
      <span className="w-[38%] truncate text-[13px] text-amp-ink dark:text-amp-dark-ink sm:w-[34%]">
        {row.store_name}
      </span>
      {/* Two half-width lanes meeting at a center line. */}
      <div className="relative flex h-3 flex-1 items-center">
        <div className="flex h-full w-1/2 justify-end">
          {comp != null && !positive && (
            <div
              className="h-full rounded-l bg-red-600 dark:bg-red-400"
              style={{ width: `${widthPct}%` }}
            />
          )}
        </div>
        <div className="absolute left-1/2 top-[-2px] h-[16px] w-px bg-amp-ink-muted dark:bg-amp-dark-ink-muted" />
        <div className="flex h-full w-1/2">
          {positive && (
            <div
              className="h-full rounded-r bg-blue-600 dark:bg-blue-400"
              style={{ width: `${widthPct}%` }}
            />
          )}
        </div>
      </div>
      <span className={`w-[64px] text-right text-[13px] tabular-nums ${compColorClass(comp)}`}>
        {formatComp(comp)}
      </span>
    </li>
  );
}

function RowList({ rows, scale }: { rows: LeaderRow[]; scale: number }) {
  return (
    <ul className="flex flex-col gap-1.5">
      {rows.map((r) => (
        <BarRow key={r.store_no} row={r} scale={scale} />
      ))}
    </ul>
  );
}

export function StoreLeaderboard({ filters }: { filters: HomeFilters }) {
  const { data, isLoading, unavailable } = useHomeLeaderboard(filters);
  const [showAll, setShowAll] = useState(false);

  const stores = data?.stores ?? [];
  const { growing, needsLook } = deriveLeaderboard(stores);
  const everyone = sortAllStores(stores);
  const scale = everyone.reduce((m, r) => Math.max(m, Math.abs(r.comp ?? 0)), 0);

  const toggle =
    stores.length > 0 ? (
      <Pill
        onClick={() => setShowAll((v) => !v)}
        aria-pressed={showAll}
        variant={showAll ? 'selected' : 'outline'}
      >
        {showAll ? 'Show top and bottom 5' : 'Show all stores'}
      </Pill>
    ) : null;

  return (
    <TileShell
      label="Stores"
      title="Store leaderboard"
      caption={data ? `${data.range.label} vs. last year` : 'vs. last year'}
      action={toggle}
    >
      {isLoading ? (
        <TileSkeleton rows={6} />
      ) : unavailable ? (
        <TileMessage>{NOT_AVAILABLE}</TileMessage>
      ) : stores.length === 0 ? (
        <TileMessage>No store comparisons yet.</TileMessage>
      ) : (
        <div className="flex flex-col gap-3">
          {showAll ? (
            <RowList rows={everyone} scale={scale} />
          ) : (
            <>
              {growing.length > 0 && (
                <section>
                  <h3 className={`mb-1.5 text-[12px] font-semibold ${MUTED_TEXT}`}>Growing fastest</h3>
                  <RowList rows={growing} scale={scale} />
                </section>
              )}
              {needsLook.length > 0 && (
                <section>
                  <h3 className={`mb-1.5 text-[12px] font-semibold ${MUTED_TEXT}`}>Needs a look</h3>
                  <RowList rows={needsLook} scale={scale} />
                </section>
              )}
            </>
          )}
          {data?.note && <p className={`text-[12px] ${MUTED_TEXT}`}>{data.note}</p>}
        </div>
      )}
    </TileShell>
  );
}
